import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Badge } from "@/components/ui/PageHeader";
import { SelectField } from "@/components/ui/FormFields";
import { getMyProposals } from "@/api/interests";
import type { RefInterestStatus } from "@/api/types";

// Backend RejectReason enum → human-readable label.
const REJECT_REASON_LABELS: Record<string, string> = {
  inappropriate: "Inappropriate content",
  duplicate_existing: "Duplicate of an existing interest",
  too_vague: "Too vague",
  out_of_scope: "Out of scope",
  other: "Other",
};

export default function MyProposals() {
  const [status, setStatus] = useState<RefInterestStatus | "">("");

  const { data, isLoading } = useQuery({
    queryKey: ["my-proposals", status],
    queryFn: () =>
      getMyProposals({ status: status || undefined, limit: 50 }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="My proposals"
        subtitle="Interests you've submitted to the catalog."
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Custom Interests", href: "/lab/interests" },
          { label: "My proposals" },
        ]}
      />

      <div className="max-w-xs">
        <SelectField
          label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value as RefInterestStatus | "")}
          options={[
            { value: "", label: "All" },
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !data || data.data.total === 0 ? (
        <p className="text-sm text-gray-500">
          You haven&apos;t proposed any interests yet.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg bg-white">
          {data.data.items.map((item) => (
            <li
              key={item.id}
              className="px-4 py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-medium text-gray-900">
                  {item.emoji ? `${item.emoji} ` : ""}
                  {item.canonicalNameEn}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  <code className="font-mono">{item.code}</code> ·{" "}
                  {item.interestType} · {item.category}
                  {item.submittedAt && (
                    <>
                      {" "}
                      · submitted{" "}
                      {new Date(item.submittedAt).toLocaleDateString()}
                    </>
                  )}
                </div>
                {item.rejectReason && (
                  <div className="text-xs text-red-600 mt-1">
                    Rejected:{" "}
                    {REJECT_REASON_LABELS[item.rejectReason] ?? item.rejectReason}
                    {item.rejectReasonDetails && ` — ${item.rejectReasonDetails}`}
                  </div>
                )}
              </div>
              <Badge
                variant={
                  item.status === "approved"
                    ? "green"
                    : item.status === "rejected"
                      ? "red"
                      : "yellow"
                }
              >
                {item.status ?? "pending"}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
